import React, { createContext, useEffect, useState } from "react";

import dayjs from "dayjs";

import { apiOrderCheckoutFetch, errorAwareApiFetch } from "../apiBindings";
import { navigate } from "../components/shared/LinkComponent";
import useSnackbar from "../components/shared/Snackbar/store";
import usePrevious from "../hooks/usePrevious";
import pushToAnalytics from "../utils/analytics";
import {
    storageLocal as storage,
    storageSession as session,
} from "../utils/StorageHandler";
import Validator from "../utils/validation/Validator";
import resolveWebView from "../utils/webViewRedirect";
import { deliveryTimeOptions } from "./DeliveryTime";
import checkoutSteps from "./PelCheckoutSteps";

export const DataContext = createContext();

const PelletsCheckoutDataProvider = ({ checkoutStep, children }) => {
    const baseUrl = "/holzpellets/bestellung/";

    const [loading, setLoading] = useState(false);
    const { notify } = useSnackbar();

    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const steps = 3;
    const [editMode, setEditMode] = useState(false);

    //const zipcode = storage.get("pel:zipcode");
    const zipcode = storage.get("pel:order-zipcode");
    const [amount, setAmount] = useState(storage.getInt("pel:order-amount"));
    const [up, setUp] = useState(storage.getInt("pel:order-up", 1));

    const [stepButtonAction, setStepButtonAction] = useState(null);
    const [overlayContent, setOverlayContent] = useState(null);
    const [checkoutData, setCheckoutData] = useState(
        session.getJson("pel:checkout")
    );
    const [orderData, setOrderData] = useState(
        session.getJson("pel:order"),
        null
    );

    const previousStepIndex = usePrevious(currentStepIndex);

    const [amountFirstUnloadingPoint, setAmountFirstUnloadingPoint] = useState(
        storage.getInt("pel:amount")
    );

    const [useInvoiceAddress, setUseInvoiceAddress] = useState(
        storage.getBoolean("pel:use-invoice-address", false)
    );

    const [detailsChanged, setDetailsChanged] = useState(0);

    /**
     * Form validation errors.
     */
    const [formErrors, setFormErrors] = useState([]);

    const [selectedStreet, setSelectedStreet] = useState(
        storage.get("pel:selected-street", "")
    );

    /**
     * Data for the delivery address step.
     */
    const [deliveryAddressData, setDeliveryAddressData] = useState(
        storage.getJson("pel:delivery-address", {
            company: "",
            salutation: "Herr",
            firstname: "",
            lastname: "",
            email: "",
            phone: "",
            street: selectedStreet,
            house_number: "",
            zipcode: storage.get("pel:order-zipcode"),
            city: "",
        })
    );

    const [additionalAddresses, setAdditionalAddresses] = useState(
        storage.getJson("pel:additional-addresses", [])
    );

    const [invoiceAddress, setInvoiceAddress] = useState(
        storage.getJson("pel:invoice-address", {
            company: "",
            salutation: "Herr",
            firstname: "",
            lastname: "",
            street: "",
            zipcode: "",
            city: "",
        })
    );

    const [orderComment, setOrderComment] = useState(
        storage.get("pel:order-comment", "")
    );

    /**
     * Current chosen payment type
     */
    const [paymentType, setPaymentType] = useState(
        storage.getInt("pel:payment-type", 2)
    );

    const [paymentTypeChanged, setPaymentTypeChanged] = useState(0);

    /**
     * AGB checked
     */
    const [agbChecked, setAgbChecked] = useState(false);

    /**
     * Promotion code
     */
    const [promotionCode, setPromotionCode] = useState(
        /**
         * Just comment this session.get out and use an empty string as default value,
         * when the promotion is expired.
         */
        session.get("pel:promotion-code", "")
    );

    // check if Promotion is valid
    /*useEffect(() => {
        if (promotionCode === "Pellets2025" && amount < 2500) {
            setPromotionCode("");
        }
    }, [amount]);*/

    /**
     * When the user accepts the agb.
     */
    useEffect(() => {
        if (agbChecked) {
            clearFormError("agb");
        }
    }, [agbChecked]);

    /**
     * Newsletter checked
     */
    const [newsletterChecked, setNewsletterChecked] = useState(
        storage.getBoolean("pel:newsletter", false)
    );

    /**
     * Delivery time [standard, short, express48, express24]
     */
    const [deliveryTime, setDeliveryTime] = useState(
        storage.get(
            "pel:delivery-time",
            storage.get("pel:delivery-time", "standard")
        )
    );
    /**
     * Type of delivery time [standard, time, date]
     */
    const [deliveryTimeType, setDeliveryTimeType] = useState(
        storage.get("pel:delivery-time-type", "standard")
    );
    /**
     * Deliver time frame [7-9, 7-12, 12-16]
     */
    const [deliveryTimeFrame, setDeliveryTimeFrame] = useState(
        storage.get("pel:delivery-time-frame", "")
    );
    /**
     * Delivery time wish date.
     */
    const [deliveryTimeWishDate, setDeliveryTimeWishDate] = useState(
        storage.get("pel:delivery-time-wish-date", "")
    );

    /**
     * Hose. For Bags and Big-Bags there is no hose.
     */
    const [hose, setHose] = useState(
        storage.get(
            "pel:hose",
            storage.get("pel:product-type") === "loose" ? 1 : null
        )
    );

    /**
     * Handle the current step index.
     */
    useEffect(() => {
        // Init the checkout, if not exists.
        if (session.get("pel:checkout") === null) {
            void initCheckout();
        }

        const newIndex = checkoutSteps.findIndex(
            (step) => step.url === checkoutStep
        );

        // If we leave the delivery address step do something with it
        if (checkoutSteps[previousStepIndex]) {
            const previousUrl = checkoutSteps[previousStepIndex].url;

            if (previousUrl === "lieferadresse") {
                if (stepButtonAction === "next") {
                    storeDeliveryAddressToStorage();
                    storeInvoiceAddressToStorage();
                    // Store to server
                    storeDeliveryAddressToServer();
                }
            }
            if (previousUrl === "zahlart") {
                if (stepButtonAction === "next") {
                    storePaymentTypeToStorage();
                    // Store to server
                    storePaymentTypeToServer();
                }
            }
            if (previousUrl === "lieferdetails") {
                if (stepButtonAction === "next") {
                    storeOrderCommentToStorage();
                    storePromotionCodeToStorage();
                    storeHoseToStorage();
                    storeDeliveryTimeToStorage();
                    storeDeliveryTimeTypeToStorage();
                    storeDeliveryTimeFrameToStorage();
                    storeDeliveryTimeWishDateToStorage();
                    // Store to server
                    storeDeliveryDetailsToServer();
                } else {
                    setOrderCommentStorageToState();
                    setPromotionCodeStorageToState();
                    setHoseStorageToState();
                    setDeliveryTimeStorageToState();
                    setDeliveryTimeTypeStorageToState();
                    setDeliveryTimeFrameToState();
                    setDeliveryTimeWishDateToState();
                }
            }
            if (stepButtonAction === "next" && previousUrl === "uebersicht") {
                if (session.getJson("pel:order") === null) {
                    sendOrderToServer();
                }
            }
        }

        setCurrentStepIndex(newIndex);
        setStepButtonAction(null);
    }, [checkoutStep]);

    /**
     * When the save button in the overlay is pressed.
     */
    const overlaySave = () => {
        if (overlayContent === "zahlart") {
            storePaymentTypeToStorage();
            // Store to server
            storePaymentTypeToServer();
        }
        if (overlayContent === "lieferdetails") {
            storeOrderCommentToStorage();
            storePromotionCodeToStorage();
            storeHoseToStorage();
            storeDeliveryTimeToStorage();
            storeDeliveryTimeTypeToStorage();
            storeDeliveryTimeFrameToStorage();
            storeDeliveryTimeWishDateToStorage();
            // Store to server
            storeDeliveryDetailsToServer();
        }
        if (overlayContent === "lieferadresse") {
            storeDeliveryAddressToStorage();
            if (useInvoiceAddress) {
                storeInvoiceAddressToStorage();
            }
            // Store to server
            storeDeliveryAddressToServer();
        }
        if (overlayContent === "menge") {
            storeAmountToStorage();
            // Store to server
            storeAmountToServer();
        }
        setOverlayContent(null);
    };

    /**
     * When the back button in the overlay is pressed.
     */
    const overlayBack = () => {
        if (overlayContent === "zahlart") {
            setPaymentTypeStorageToState();
            paymentTypeHasChanged();
        }
        if (overlayContent === "lieferdetails") {
            setOrderCommentStorageToState();
            setPromotionCodeStorageToState();
            setHoseStorageToState();
            setDeliveryTimeStorageToState();
            setDeliveryTimeTypeStorageToState();
            setDeliveryTimeFrameToState();
            setDeliveryTimeWishDateToState();
            detailsHasChanged();
        }
        if (overlayContent === "lieferadresse") {
            setDeliveryAddressStorageToState();
            setInvoiceAddressStorageToState();
        }
        if (overlayContent === "menge") {
            setAmountStorageToState();
        }
        setOverlayContent(null);
    };

    /**
     * Check if the previous step should be accessable through the back button.
     */
    const previousStepVisible = () => {
        if (currentStepIndex > 0) {
            return true;
        }

        return false;
    };

    const onGooglePlacesChange = (placesData, isInvoiceAddress = false) => {
        const result = placesData[0];
        result.address_components.forEach((address) => {
            if (address.types.includes("route")) {
                if (isInvoiceAddress) {
                    setInvoiceAddress((currentAddress) => {
                        return {
                            ...currentAddress,
                            street: address.long_name,
                        };
                    });
                } else {
                    setDeliveryAddressData((currentAddress) => {
                        return {
                            ...currentAddress,
                            street: address.long_name,
                        };
                    });
                }
            }
            if (address.types.includes("street_number")) {
                if (isInvoiceAddress) {
                    setInvoiceAddress((currentAddress) => {
                        return {
                            ...currentAddress,
                            house_number: address.long_name,
                        };
                    });
                } else {
                    setDeliveryAddressData((currentAddress) => {
                        return {
                            ...currentAddress,
                            house_number: address.long_name,
                        };
                    });
                }
            }
            if (address.types.includes("locality")) {
                if (isInvoiceAddress) {
                    setInvoiceAddress((currentAddress) => {
                        return {
                            ...currentAddress,
                            city: address.long_name,
                        };
                    });
                } else {
                    setDeliveryAddressData((currentAddress) => {
                        return {
                            ...currentAddress,
                            city: address.long_name,
                        };
                    });
                }
            }
            if (address.types.includes("postal_code") && isInvoiceAddress) {
                setInvoiceAddress((currentAddress) => {
                    return {
                        ...currentAddress,
                        zipcode: address.long_name,
                    };
                });
            }
        });
    };

    /**
     * Go to the next step and adjust the router.
     */
    const nextStep = () => {
        if (currentStepIndex + 1 < checkoutSteps.length) {
            const currentStep = checkoutSteps[currentStepIndex].url;
            const nextStep = baseUrl + checkoutSteps[currentStepIndex + 1].url;
            if (currentStep === "lieferadresse") {
                if (!deliveryAddressStepIsValid()) {
                    return;
                }
                void navigate(resolveWebView(nextStep));
            }
            if (currentStep === "uebersicht") {
                if (!agbChecked) {
                    addFormError("agb");
                    return;
                }
            }

            if (formErrors.length === 0) {
                void navigate(resolveWebView(nextStep));
            }
        }
    };

    /**
     * Go to the previous step and adjust the router.
     */
    const previousStep = () => {
        void navigate(
            resolveWebView(baseUrl + checkoutSteps[currentStepIndex - 1].url)
        );
    };

    /**
     * Returns the current prices.
     */
    const getPrices = () => {
        if (!checkoutData) {
            return null;
        }

        return {
            priceNet: checkoutData.price_net,
            priceGross: checkoutData.price_gross,
            priceTotalNet: checkoutData.price_total_net,
            priceTotalGross: checkoutData.price_total_gross,
        };
    };

    /**
     * When the details changed (state) the price should be updated.
     */
    useEffect(() => {
        if (detailsChanged > 0) {
            storeDeliveryDetailsToServer(true);
        }
    }, [detailsChanged]);

    /**
     * When the payment type changed (state) the price should be updated.
     */
    useEffect(() => {
        if (paymentTypeChanged > 0) {
            storePaymentTypeToServer(true);
        }
    }, [paymentTypeChanged]);

    /**
     * ###################################################################
     *   Form validation.
     * ###################################################################
     */
    /**
     * Add an error to the errors.
     *
     * @param {*} key
     * @param {*} value
     */
    const addFormError = (key) => {
        setFormErrors([...formErrors, key]);
    };

    /**
     * Remove an element from the errors.
     *
     * @param {*} key
     */
    const clearFormError = (key) => {
        setFormErrors(formErrors.filter((item) => item !== key));
    };

    /**
     * Check if the given key is present in the errors.
     *
     * @param {*} key
     */
    const hasFormErrorAt = (key) => {
        return formErrors.filter((item) => item === key).length > 0;
    };

    /**
     * Change the selected street.
     */
    const changeSelectedStreet = (street) => {
        setDeliveryAddressData({
            ...deliveryAddressData,
            street: street,
        });
        clearFormError("street");
        storage.set("pel:selected-street", street);
    };

    const changeSelectedCity = (city) => {
        if (city) {
            setDeliveryAddressData({
                ...deliveryAddressData,
                city: city,
            });
            clearFormError("city");
            storage.set("pel:selected-city", city);
        }
    };

    /**
     * ###################################################################
     *   Functions to store the values from state to local storage.
     * ###################################################################
     */

    /**
     * Stores the amount the storage.
     */
    const storeAmountToStorage = () => {
        storage.set("pel:amount", amount);
    };

    /**
     * Stores the delivery address in the storage.
     */
    const storeDeliveryAddressToStorage = () => {
        storage.setJson("pel:delivery-address", deliveryAddressData);
        storage.setJson("pel:additional-addresses", additionalAddresses);
    };

    /**
     * Stores the invoice informations to storage.
     */
    const storeInvoiceAddressToStorage = () => {
        storage.setJson("pel:invoice-address", invoiceAddress);
        storage.set("pel:use-invoice-address", useInvoiceAddress);
    };

    /**
     * Store the payment type in the storage.
     */
    const storePaymentTypeToStorage = () => {
        storage.set("pel:payment-type", paymentType);
    };

    /**
     * Store the order comment in the storage.
     */
    const storeOrderCommentToStorage = () => {
        storage.set("pel:order-comment", orderComment);
    };

    /**
     * Store the promotion code in the storage.
     */
    const storePromotionCodeToStorage = () => {
        session.set("pel:promotion-code", promotionCode);
    };

    /**
     * Store the hose choice in the storage.
     */
    const storeHoseToStorage = () => {
        storage.set("pel:hose", hose);
    };

    /**
     * Store the delivery time choice in the storage.
     */
    const storeDeliveryTimeToStorage = () => {
        storage.set("pel:delivery-time", deliveryTime);
    };

    /**
     * Store the delivery time type choice in the storage.
     */
    const storeDeliveryTimeTypeToStorage = () => {
        storage.set("pel:delivery-time-type", deliveryTimeType);
    };

    /**
     * Store the delivery time frame choice in the storage.
     */
    const storeDeliveryTimeFrameToStorage = () => {
        if (deliveryTimeType !== deliveryTimeOptions.time) {
            storage.set("pel:delivery-time-frame", "");
            setDeliveryTimeFrame("");
        } else {
            storage.set("pel:delivery-time-frame", deliveryTimeFrame);
        }
    };

    /**
     * Store the delivery time wish date in the storage.
     */
    const storeDeliveryTimeWishDateToStorage = () => {
        if (deliveryTimeType !== deliveryTimeOptions.date) {
            storage.set("pel:delivery-time-wish-date", "");
            setDeliveryTimeWishDate("");
        } else {
            storage.set("pel:delivery-time-wish-date", deliveryTimeWishDate);
        }
    };

    /**
     * ###################################################################
     *   Functions to store the values from local storage to the state.
     * ###################################################################
     */

    /**
     * Sets the amount from storage to state.
     */
    const setAmountStorageToState = () => {
        setAmount(storage.get("pel:order-amount"));
    };

    /**
     * Sets the delivery address from storage to the state.
     */
    const setDeliveryAddressStorageToState = () => {
        setDeliveryAddressData(storage.getJson("pel:delivery-address"));
        setAdditionalAddresses(storage.getJson("pel:additional-addresses"));
    };

    /**
     * Set the invoice informations from storage to the state.
     */
    const setInvoiceAddressStorageToState = () => {
        setInvoiceAddress(storage.getJson("pel:invoice-address"));
        setUseInvoiceAddress("pel:use-invoice-address");
    };
    /**
     * Set the payment type from storage to the state.
     */
    const setPaymentTypeStorageToState = () => {
        setPaymentType(storage.getInt("pel:payment-type"));
    };

    /**
     * Set the order comment from storage to the state.
     */
    const setOrderCommentStorageToState = () => {
        setOrderComment(storage.get("pel:order-comment", ""));
    };

    /**
     * Set the promotion code from storage to the state.
     */
    const setPromotionCodeStorageToState = () => {
        setPromotionCode(session.get("pel:promotion-code", ""));
    };

    /**
     * Set hose from storage to the state
     */
    const setHoseStorageToState = () => {
        setHose(
            storage.get(
                "pel:hose",
                storage.get("pel:product-type") === "loose" ? 1 : null
            )
        );
    };

    /**
     * Set delivery time from storage to the state
     */
    const setDeliveryTimeStorageToState = () => {
        setDeliveryTime(storage.get("pel:delivery-time", "standard"));
    };

    /**
     * Set delivery time type from storage to the state
     */
    const setDeliveryTimeTypeStorageToState = () => {
        setDeliveryTimeType(storage.get("pel:delivery-time-type", "standard"));
    };

    /**
     * Set the delivery time frame choice to the storage.
     */
    const setDeliveryTimeFrameToState = () => {
        setDeliveryTimeFrame(storage.get("pel:delivery-time-frame"));
    };

    /**
     * Store the delivery time wish date to the storage.
     */
    const setDeliveryTimeWishDateToState = () => {
        setDeliveryTimeWishDate(storage.get("pel:delivery-time-wish-date"));
    };

    /**
     * When details has changed, then store them to the storage. Relevan for the overlay
     */
    const detailsHasChanged = () => {
        setDetailsChanged(detailsChanged + 1);
    };

    /**
     * Indicator for payment type has changed.
     */
    const paymentTypeHasChanged = () => {
        setPaymentTypeChanged(paymentTypeChanged + 1);
    };

    /**
     * Check if the delivery address step is valid.
     */
    const deliveryAddressStepIsValid = () => {
        let errors = [...formErrors];
        const validator = new Validator();
        const phoneIsValid = validator.getValidator(
            "phone",
            deliveryAddressData.phone,
            true
        );

        const emailIsValid = validator.getValidator(
            "email",
            deliveryAddressData.email,
            true
        );

        // Check if the fields are filled for delivery address
        if (
            deliveryAddressData.firstname === "" ||
            deliveryAddressData.firstname === undefined
        ) {
            errors.push("firstname");
        }
        if (
            deliveryAddressData.lastname === "" ||
            deliveryAddressData.lastname === undefined
        ) {
            errors.push("lastname");
        }
        if (!phoneIsValid()) {
            errors.push("phone");
        }
        if (!emailIsValid()) {
            errors.push("email");
        }
        if (deliveryAddressData.street === "") {
            errors.push("street");
        }
        if (
            !deliveryAddressData.house_number ||
            deliveryAddressData.house_number === ""
        ) {
            errors.push("house_number");
        }
        if (deliveryAddressData.city === "") {
            errors.push("city");
        }

        // Check if the fields are filled for invoice address
        if (useInvoiceAddress) {
            if (invoiceAddress.firstname === "") {
                errors.push("inv_firstname");
            }
            if (invoiceAddress.lastname === "") {
                errors.push("inv_lastname");
            }
            if (invoiceAddress.street === "") {
                errors.push("inv_street");
            }
            if (invoiceAddress.zipcode === "") {
                errors.push("inv_zipcode");
            }
            if (invoiceAddress.city === "") {
                errors.push("inv_city");
            }
            if (invoiceAddress.house_number === "") {
                errors.push("inv_house_number");
            }
        } else {
            errors = errors.filter((error) => !error.startsWith("inv_"));
        }

        setFormErrors(errors);

        if (errors.length > 0) {
            return false;
        }

        return true;
    };

    /**
     * ###################################################################
     *   Functions which communicate with the server.
     * ###################################################################
     */

    /**
     * Init the checkout process.
     */
    const initCheckout = async () => {
        const product = storage.get("pel:order-product-id");
        const productType = storage.get("pel:order-product-type");
        const orderZipcode = storage.get("pel:order-zipcode");
        const orderAmount = storage.get("pel:order-amount");

        try {
            const data = await errorAwareApiFetch(
                `/pellets/checkout/init/${orderZipcode}/${orderAmount}/${productType}/${product}`
            );

            session.setJson("pel:checkout", data);
            storage.set("pel:needs-session", true);
            setCheckoutData(data);
            setDeliveryAddressData({
                ...deliveryAddressData,
                ...{ zipcode: orderZipcode, city: "", street: "" },
            });
            storage.setJson("pel:delivery-address", {
                ...deliveryAddressData,
                ...{ zipcode: orderZipcode, city: "", street: "" },
            });
            return data;
        } catch (error) {
            const msg =
                error?.data?.message ??
                "Beim Initialisieren des Bestellprozesses ist ein Fehler aufgetreten. Bitte versuche es erneut.";
            await navigate(resolveWebView("/holzpellets"));
            void notify("Fehler", msg, "default", { timeout: 10000 });
        }
    };

    /**
     * Store the delivery address data to the server.
     */
    const storeDeliveryAddressToServer = () => {
        const data = {
            hash: checkoutData.user_hash,
            deliveryAddress: deliveryAddressData,
            additionalAddresses: additionalAddresses,
            useInvoiceAddress: useInvoiceAddress,
            invoiceAddress: invoiceAddress,
        };

        apiRequest("deliveryAddress", data);
    };

    /**
     * Store the delivery details to the server.
     */
    const storeDeliveryDetailsToServer = (calculateOnly = false) => {
        const data = {
            hash: checkoutData.user_hash,
            hose,
            deliveryTime,
            deliveryTimeFrame:
                deliveryTimeType !== deliveryTimeOptions.time ||
                deliveryTimeFrame === null
                    ? ""
                    : deliveryTimeFrame,
            deliveryTimeWishDate:
                deliveryTimeType !== deliveryTimeOptions.date
                    ? ""
                    : deliveryTimeWishDate,
            comment: orderComment,
            promotionCode,
            ...(calculateOnly ? { calculateOnly } : {}),
        };
        const onSuccess = (data) => {
            const newData = { ...checkoutData, ...data };
            if (!calculateOnly) {
                session.setJson("pel:checkout", newData);
            }
            setCheckoutData(newData);
        };

        apiRequest("deliveryDetails", data, onSuccess);
    };

    /**
     * Store the payment type to the server.
     */
    const storePaymentTypeToServer = (calculateOnly = false) => {
        const data = {
            hash: checkoutData.user_hash,
            paymentType,
            ...(calculateOnly ? { calculateOnly } : {}),
        };
        const onSuccess = (data) => {
            const newData = { ...checkoutData, ...data };
            if (!calculateOnly) {
                session.setJson("pel:checkout", newData);
            }
            setCheckoutData(newData);
        };

        apiRequest("paymentType", data, onSuccess);
    };

    /**
     * Store the new amount to the server
     */
    const storeAmountToServer = () => {
        const data = {
            hash: checkoutData.user_hash,
            amount,
        };
        const onSuccess = (data) => {
            const newData = { ...checkoutData, ...data };
            session.setJson("pel:checkout", newData);
            setCheckoutData(newData);
        };

        apiRequest("amount", data, onSuccess);
    };

    /**
     * Creates an order from the checkout
     */
    const sendOrderToServer = () => {
        const data = {
            hash: checkoutData.user_hash,
            newsletter: newsletterChecked,
        };
        apiRequest("finish", data, (data) => {
            session.setJson("pel:order", data.order);
            storage.set("app:uid", data.appUid);

            pushToAnalytics("order", {
                orderNumber: data.order.order_number,
            });

            setOrderData(data.order);
        });
    };

    /**
     * Send a request to the api.
     *
     * @param {*} endpoint
     * @param {*} payload
     * @param {*} onSuccess
     */
    const apiRequest = (endpoint, payload, onSuccess) => {
        setLoading(true);
        apiOrderCheckoutFetch(`/pellets/checkout/${endpoint}`, {
            method: "post",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: JSON.stringify(payload),
        })
            .then((response) => response.json())
            .then((data) => {
                if (onSuccess) {
                    onSuccess(data);
                }
                setLoading(false);
            });
    };

    const data = {
        steps,
        currentStepIndex,
        editMode,
        formErrors,
        deliveryAddressData,
        up,
        amount,
        amountFirstUnloadingPoint,
        useInvoiceAddress,
        invoiceAddress,
        deliveryTime,
        deliveryTimeType,
        deliveryTimeFrame,
        deliveryTimeWishDate,
        stepButtonAction,
        orderComment,
        hose,
        paymentType,
        overlayContent,
        agbChecked,
        newsletterChecked,
        orderData,
        loading,
        promotionCode,
    };

    const actions = {
        previousStepVisible,
        nextStep,
        previousStep,
        setEditMode,
        addFormError,
        clearFormError,
        hasFormErrorAt,
        setDeliveryAddressData,
        setUp,
        setAmount,
        setAmountFirstUnloadingPoint,
        setUseInvoiceAddress,
        setInvoiceAddress,
        setDeliveryTime,
        setDeliveryTimeType,
        setDeliveryTimeFrame,
        setDeliveryTimeWishDate,
        detailsHasChanged,
        paymentTypeHasChanged,
        setStepButtonAction,
        setOrderComment,
        setHose,
        setPaymentType,
        setOverlayContent,
        getPrices,
        setAgbChecked,
        setNewsletterChecked,
        overlaySave,
        overlayBack,
        setPromotionCode,
        changeSelectedStreet,
        changeSelectedCity,
        onGooglePlacesChange,
    };

    return (
        <DataContext.Provider
            value={{
                data,
                actions,
            }}
        >
            {children}
        </DataContext.Provider>
    );
};

export default PelletsCheckoutDataProvider;
